import { useMemo, VFC } from 'react';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionSendSlackMessageV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { JsonSchemaStringifiableTypes } from 'lib/fields';
import { JsonSchemaTypes } from 'lib/json-schema';

interface ParamsSendSlackMessageV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionSendSlackMessageV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionSendSlackMessageV1['params']) => void;
}

const ParamsSendSlackMessageV1: VFC<ParamsSendSlackMessageV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const updateSendSlackMessageParams = <
    Key extends keyof WorkflowActionSendSlackMessageV1['params'],
  >(
    key: Key,
    value: WorkflowActionSendSlackMessageV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionSendSlackMessageV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='URL (Slack webhook URL)' htmlFor='params-send-slack-message__url' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['url']}
        updateValue={(value: string) => {
          updateSendSlackMessageParams('url', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        placeholder='https://hooks.slack.com/...'
        id='params-send-slack-message__url'
        hasError={hasErrorFor('url')}
      />
      {renderErrorMessageFor('url')}
      <Spacer height={2} />
      <ParamLabel labelText='Message' htmlFor='params-send-slack-message__message' />
      <HandlebarFormControl
        size='md'
        type='text-area'
        value={params['message']}
        updateValue={(value: string) => {
          updateSendSlackMessageParams('message', value);
        }}
        editable={editable}
        allowedTypes={JsonSchemaStringifiableTypes}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-send-slack-message__message'
        hasError={hasErrorFor('message')}
      />
      {renderErrorMessageFor('message')}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        {' '}
        <Spacer height={2} />
        <ParamLabel
          labelText='Recipient Override (Optional)'
          htmlFor='params-send-slack-message__override'
        />
        <HandlebarFormControl
          size='md'
          type='input'
          value={params['override'] || ''}
          updateValue={(value: string) => {
            updateSendSlackMessageParams('override', value);
          }}
          editable={editable}
          allowedTypes={[JsonSchemaTypes.String]}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          placeholder='i.e. #Public-Channel / @User'
          data-test='params-send-slack-message__override'
          id='params-send-slack-message__override'
        />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsSendSlackMessageV1;
